import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { PUBLIC_SITE_DOMAIN, createProject, draftProjectWithAi, getAccountUsage, getPublicTemplateData, isPlanLimitError, listPublicTemplates, projectAddress, serverErrorCode, type BillingPlan, type PublicTemplate } from '../../lib/api'
import { translateServerError, useI18n } from '../../lib/i18n'
import { SitePreview } from './SitePreview'
import { withStartRecipe } from '../../lib/siteDesign'
import { composeBusinessInfo, composeElements, composePreviewPage, composeProjectData, extraPagesOf, hydrateForm, hydrateFromTemplate, brandPaletteOf, recipeWithBrand, siteMotionOf, slugify, suggestedMotion, themeForRecipe, type CreateForm } from '../../lib/preview/compose'
import type { ElementNode } from '../../lib/preview/types'
import { clearAiDraft, readAiDraft, saveAiDraft } from '../../lib/aiDraftStore'
import { clearWizardPhotos, readWizardPhotos, useWizardPhotos, wizardPhotoLimit } from '../../lib/wizardPhotos'
import { DEFAULT_PAGE_THEME, DESIGN_STYLES, STYLE_PALETTE_PREFIX, normalizeProjectTheme, type CustomPalette, type ProjectTheme as ThemeProjectTheme } from '@webbuilder/theme'
import { ColorsStep, CombinationStep, FontsStep, MotionStep, StylePresetsStep, comboRecipe } from './WizardStyleStep'
import { WizardSectionsStep } from './WizardSectionsStep'
import { hasMapWithoutAddress, missingContactKinds, showsBusinessHours } from '@webbuilder/schemas'
import { WizardContactStep } from './WizardContactStep'
import { industryIdeas } from '../../lib/promptIdeas'
import { WELCOME_BUSINESSES, WELCOME_BUSINESS_LABELS, readWelcome, saveWelcome, templateFitsBusiness, welcomeBrief, type WelcomeAnswers, type WelcomeBusiness } from '../../lib/welcomeAnswers'
import { PROMPT_TEMPLATES } from './promptConfig'
import { PromptComposer } from './PromptComposer'
import { IdeaShuffle } from './IdeaShuffle'
import {
  IconAddressBook,
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconBuildingStore,
  IconCheck,
  IconClockHour4,
  IconColorSwatch,
  IconInfoCircle,
  IconKeyframes,
  IconLayoutBoard,
  IconLayoutRows,
  IconLoader2,
  IconPalette,
  IconPhotoPlus,
  IconSparkles,
  IconRefresh,
  IconTrash,
  IconTypography,
  IconX,
} from '@tabler/icons-react'
import { editorHref } from '../../lib/editorSession'
import { AiMascot } from './AiMascot'
import { BrandBackPill } from '../BrandBackPill'
import { WizardTopBar } from '../ProjectWorkspace/WizardTopBar'
import { ImageLibraryPickerDialog } from '../ImageLibraryPickerDialog'
import { useScrollTopOnStep } from '../../lib/useScrollTopOnStep'

/**
 * Los pasos, uno por decisión.
 *
 * Eran cuatro —datos, estilo, secciones y marca— y el de estilo llevaba seis
 * pestañas dentro: el color, la letra o los botones sólo se encontraban si se
 * sabía que había que buscarlos. Ahora cada decisión es su paso, con su raíl,
 * como los demás asistentes del panel. Claves y no texto: constante de módulo.
 */
type StepKey = 'template' | 'info' | 'combination' | 'colors' | 'fonts' | 'style' | 'motion' | 'sections' | 'contact'

const STEPS: { key: StepKey; labelKey: string; Icon: typeof IconInfoCircle }[] = [
  { key: 'info', labelKey: 'cwa.step.business', Icon: IconBuildingStore },
  /* Primero la combinación entera —la de la IA u otra—, y luego se ajusta
     por partes en los pasos que siguen. */
  { key: 'combination', labelKey: 'cwa.step.combination', Icon: IconColorSwatch },
  { key: 'colors', labelKey: 'cwa.step.colors', Icon: IconPalette },
  { key: 'fonts', labelKey: 'cwa.step.type', Icon: IconTypography },
  /* El estilo, después del color y la letra: sólo cambia la forma —botones,
     tarjetas, aire— y deja lo elegido antes como estaba. */
  { key: 'style', labelKey: 'cwa.step.style', Icon: IconSparkles },
  /* El movimiento del sitio: uno de los estilos de Marca › Animación, sin ajustes. */
  { key: 'motion', labelKey: 'cwa.step.motion', Icon: IconKeyframes },
  { key: 'sections', labelKey: 'cwa.step.sections', Icon: IconLayoutRows },
  { key: 'contact', labelKey: 'cwa.step.contact', Icon: IconAddressBook },
]

/**
 * Los pasos desde un template de sitio: primero se elige el diseño y después
 * lo mismo que con IA —nombre, colores, letra, estilo y contacto—. Sin
 * «Secciones»: las del template no tienen variantes que elegir.
 */
const TEMPLATE_STEPS: { key: StepKey; labelKey: string; Icon: typeof IconInfoCircle }[] = [
  { key: 'template', labelKey: 'cwa.step.template', Icon: IconLayoutBoard },
  /* Ni «Combinación»: su primera tarjeta es la que eligió la IA, y aquí el
     punto de partida es el template. */
  ...STEPS.filter((item) => item.key !== 'sections' && item.key !== 'combination'),
]

/**
 * Los pasos con un diseño traído de la demo pública (`/demo`): ya viene con su
 * combinación, sus colores y sus secciones, así que se empieza por el nombre y
 * lo demás queda para repasarlo. Con «Secciones», que sí tienen variantes.
 */
const DEMO_STEPS = STEPS

/** El tema sobre el que se dibujan las combinaciones con IA: el de por defecto, como en `themeForRecipe`. Uno solo, para no rehacer las 34 muestras en cada render. */
let defaultBase: ThemeProjectTheme | null = null
const combinationBaseOf = (form: CreateForm): ThemeProjectTheme => (form.baseTheme as unknown as ThemeProjectTheme | undefined)
  ?? (defaultBase ??= normalizeProjectTheme(DEFAULT_PAGE_THEME))

/**
 * La paleta del estilo de la combinación, tal como viene, para el paso de
 * colores: «por si se arrepiente». Sin ella si es la misma que la de la IA.
 */
function stylePreset(form: CreateForm, t: (key: string) => string): { palette: CustomPalette; id: string; name: string } | null {
  const styleId = form.combination ?? (form.aiRecipe ?? form.recipe).designStyleId
  const style = DESIGN_STYLES.find((item) => item.id === styleId)
  if (!style) return null
  const id = STYLE_PALETTE_PREFIX + style.id
  if (id === form.aiPaletteId) return null
  return { palette: brandPaletteOf(comboRecipe(style.id)), id, name: t(style.labelKey) }
}

/** Lo que se va contando mientras la IA trabaja: tarda, y un giro solo no dice nada. */
const GENERATING_STEPS = ['cwa.wz.generating.plan', 'cwa.wz.generating.copy', 'cwa.wz.generating.sections', 'cwa.wz.generating.images', 'cwa.wz.generating.style'] as const

/**
 * La mascota del primer paso. El hexágono se mantiene estable y sólo la mirada
 * sigue al puntero. El seguimiento usa interpolación y variables CSS para no
 * hacer render de React en cada movimiento del mouse.
 */
export function CreateWithAiModal({ open, token, onClose, onExit, onPlanLimit, onCreated, initialPrompt = '', mode = 'ai', userId, resume = false, initialForm = null, startRecipe = null }: {
  open: boolean
  /** Para guardar el borrador de este usuario y retomarlo (`aiDraftStore`). */
  userId?: string
  /** Abrir con el borrador guardado en vez de generar uno nuevo. */
  resume?: boolean
  /** El proyecto recién creado, justo antes de salir al editor. */
  onCreated?: (projectId: string) => void
  /**
   * `template`: sin IA, desde un template de sitio que se elige en el primer paso.
   * `demo`: sin IA, con el diseño que se armó en la demo pública (`initialForm`).
   */
  mode?: 'ai' | 'template' | 'demo'
  /** El diseño de la demo, en el modo `demo`. */
  initialForm?: CreateForm | null
  /** El estilo elegido en veltica.com antes de entrar: lo que se genere o el template que se elija empieza con él (`withStartRecipe`). */
  startRecipe?: CreateForm['recipe'] | null
  token: string
  onClose: () => void
  /**
   * Salir del todo, al panel.
   *
   * Es lo que hace la pastilla de la barra, y no es lo mismo que `onClose`: la
   * X cierra este camino y devuelve a «Comienza tu proyecto», que está justo
   * debajo. Dos salidas a distancias distintas.
   */
  onExit?: (() => void) | undefined
  /**
   * El cupo de proyectos de la membresía, lleno.
   *
   * Lo dice el servidor al crear, y no se enseña aquí: quien lo recibe cierra
   * este camino y saca el aviso en el panel, que es donde se ofrece mejorar el
   * plan. Sin esto, el tope aparecía como un renglón rojo con el JSON del error.
   */
  onPlanLimit?: (() => void) | undefined
  initialPrompt?: string
}) {
  const { t, fmt, language } = useI18n()
  /* Arranca en `ready` y no en `loading`: sin descripción no hay nada
     generándose todavía, y el giro antes del primer paso hacía creer que el
     asistente ya estaba trabajando. */
  /* `done`: la IA ya contestó y el asistente se monta DEBAJO —su vista previa
     empieza a cargar— mientras encima la pantalla de carga dice «creado» un
     momento y se desvanece. Ver `generate`. */
  const [status, setStatus] = useState<'loading' | 'done' | 'ready' | 'error'>('ready')
  const [leaving, setLeaving] = useState(false)
  const doneTimers = useRef<number[]>([])
  useEffect(() => () => { doneTimers.current.forEach((id) => window.clearTimeout(id)) }, [])
  const [error, setError] = useState<string | null>(null)
  /* El código del servidor, aparte del texto: el tope diario no se arregla
     reintentando, y la pantalla tiene que decirlo y ofrecer otra salida. */
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [form, setForm] = useState<CreateForm | null>(null)
  const steps = mode === 'template' ? TEMPLATE_STEPS : mode === 'demo' ? DEMO_STEPS : STEPS
  const firstStep: StepKey = mode === 'template' ? 'template' : 'info'
  const [step, setStep] = useState<StepKey>(firstStep)
  /* Sube con cada clic en una tarjeta de «Animación»: la vista previa la reproduce. */
  const [motionReplay, setMotionReplay] = useState(0)
  /* Los templates de sitio y el elegido, en el modo template. */
  const [templates, setTemplates] = useState<PublicTemplate[] | null>(null)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  /* Cuántas generaciones le quedan, para decirlo junto a «Crear»: es ahí, y
     no al generar, donde se gasta una. `null`: sin tope o sin dato. */
  const [credits, setCredits] = useState<{ left: number; total: number; resetAt: string | null } | null>(null)
  /* Lo que dijo el servidor de esta generación: si ya se cobró al generarla
     (pasó de las pruebas gratis) y cuántas pruebas gratis quedan. */
  const [generationCost, setGenerationCost] = useState<{ charged: boolean; freeTriesLeft: number | null }>({ charged: false, freeTriesLeft: null })
  /* El plan, para el tope de fotos propias. */
  const [plan, setPlan] = useState<BillingPlan | null>(null)
  /* El paso más lejano al que se ha llegado. Los pasos se recorren en orden
     —crear es el último, no un atajo desde cualquiera—; el raíl deja volver a
     los ya vistos, no saltar a los que faltan. */
  const [furthest, setFurthest] = useState(0)
  const [pickingLogo, setPickingLogo] = useState(false)
  const [generatingAt, setGeneratingAt] = useState(0)
  const railRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  /* ── En el celular: la página detrás y el asistente como hoja (2026-10-30) ──
     Bajo 1088 px la vista previa se escondía y la página no se veía. Ahora va
     a pantalla completa, en su ancho de teléfono, y el paso sube desde abajo
     en tres alturas: plegada (sólo el asa y Atrás/Siguiente, para recorrer la
     página), media (las opciones en fila, que se deslizan) y completa (los
     pasos de escribir). Cada paso abre en la suya; el asa la cambia. */
  const compact = useCompactWizard()
  const formRef = useRef<HTMLFormElement>(null)
  const [sheet, setSheet] = useState<WizardSheet>(() => sheetFor(firstStep))
  useEffect(() => { setSheet(sheetFor(step)) }, [step])
  /* Tocar la página (el foco pasa a su marco) pliega la hoja. */
  useEffect(() => {
    if (!compact) return
    const onBlur = () => { window.setTimeout(() => { if (document.activeElement instanceof HTMLIFrameElement) setSheet('peek') }, 0) }
    window.addEventListener('blur', onBlur)
    return () => window.removeEventListener('blur', onBlur)
  }, [compact])
  /* La rueda del ratón sobre una fila de opciones la desplaza de lado (en el
     celular o en una ventana estrecha, donde van en fila). */
  useEffect(() => {
    const form = formRef.current
    if (!compact || !form) return
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      const list = (event.target as Element | null)?.closest?.(SHEET_SLIDERS) as HTMLElement | null
      if (!list || list.scrollWidth <= list.clientWidth + 1 || getComputedStyle(list).overflowX === 'visible') return
      list.scrollLeft += event.deltaY
      event.preventDefault()
    }
    form.addEventListener('wheel', onWheel, { passive: false })
    return () => form.removeEventListener('wheel', onWheel)
  }, [compact, sheet])
  /* La vista previa y las tarjetas de Secciones se siguen: la sección que se
     ve marca su tarjeta, y una tarjeta puede llevar la página a su sección
     (`nonce`: pedir la misma dos veces también la vuelve a enseñar). */
  const [previewSection, setPreviewSection] = useState<string | null>(null)
  /* La página que se ve en la vista previa y cuyas secciones lista el paso de
     secciones (0 = la portada). La cambian sus píldoras y los enlaces de la
     propia vista previa. */
  const [previewPage, setPreviewPage] = useState(0)
  /* Otro borrador u otro template: de vuelta a la portada. */
  const formBase = form?.base
  useEffect(() => { setPreviewPage(0) }, [formBase])
  const [reveal, setReveal] = useState<{ rootId: string; nonce: number } | null>(null)
  /* La dirección de verdad, ya libre, sacada del nombre. Aquí no se elige: es
     la que se enseña en la vista previa y la que se reserva al crear. Si la
     del nombre está ocupada se usa una libre, sin preguntar —quien crea aún
     no ha escrito ninguna—. Se elige después, en Dominio o al publicar, y ahí
     sí se avisa si no está disponible. */
  const [reservedAddress, setReservedAddress] = useState<string | null>(null)
  const startedRef = useRef(false)
  const promptFieldRef = useRef<HTMLTextAreaElement | null>(null)
  /* Lo que se le va a pedir a la IA. Vive aquí, y no fuera, porque describir el
     sitio ES el primer paso de este asistente: cuando el campo estaba en la
     portada, el modal se abría ya generando y la descripción quedaba fuera de
     lo que el usuario entiende por «el wizard». Se puede llegar con ella escrita
     —`initialPrompt`— y entonces se salta el paso. */
  const [prompt, setPrompt] = useState(initialPrompt)

  /* Un ejemplo pedido con «Prueba un ejemplo»: se escribe entero y la caja lo
     hace aparecer palabra a palabra (`revealKey` en `PromptComposer`). */
  const [revealKey, setRevealKey] = useState(0)
  function showExample(nextPrompt: string) {
    setPrompt(nextPrompt)
    setRevealKey((key) => key + 1)
    // En móvil, enfocar el textarea abre el teclado y Safari amplía la página.
    // El ejemplo ya aparece en el campo; se puede tocar para editarlo después.
    if (!window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
      promptFieldRef.current?.focus()
    }
  }

  /* El mensaje de mientras se genera cambia cada pocos segundos. */
  useEffect(() => {
    if (status !== 'loading') return
    setGeneratingAt(0)
    const timer = window.setInterval(() => setGeneratingAt((at) => at + 1), 2600)
    return () => window.clearInterval(timer)
  }, [status])

  /* Dónde va la pastilla del raíl. Medida y no calculada: «Colores» y
     «Secciones» no miden igual. El mismo gesto que el de ofertas. */
  /* Cada paso empieza arriba: se llega con «Siguiente», que está al pie, y el
     paso nuevo se abría por donde acababa el anterior. */
  useScrollTopOnStep(step, scrollRef)

  const hasForm = form !== null
  useLayoutEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const place = () => {
      const active = rail.querySelector<HTMLButtonElement>('button.is-current')
      if (!active) return
      rail.style.setProperty('--step-x', `${active.offsetLeft}px`)
      rail.style.setProperty('--step-w', `${active.offsetWidth}px`)
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [step, hasForm, status])

  // Lock the dashboard scroll behind the full-screen wizard (avoids a second scrollbar).
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])


  const patch = (p: Partial<CreateForm>) => setForm((f) => (f ? { ...f, ...p } : f))
  /* Los datos de contacto que enseñan las páginas y aún faltan: el paso de
     contacto los pone y los marca (ver `WizardContactStep`). */
  const missingContacts = useMemo(() => {
    if (!form || step !== 'contact') return []
    const nodes = [composeElements(form), ...extraPagesOf(form).filter((page) => page.included).map((page) => composeElements(form, page))].flat()
    return missingContactKinds(nodes as never, form.contacts)
  }, [form, step])
  /* Si alguna página enseña un horario (el de muestra, si no se da el suyo). */
  const pageShowsHours = useMemo(() => {
    if (!form || step !== 'contact') return false
    const nodes = [composeElements(form), ...extraPagesOf(form).filter((page) => page.included).map((page) => composeElements(form, page))].flat()
    return showsBusinessHours(nodes as never)
  }, [form, step])
  const mapNeedsAddress = useMemo(() => {
    if (!form || step !== 'contact') return false
    const nodes = [composeElements(form), ...extraPagesOf(form).filter((page) => page.included).map((page) => composeElements(form, page))].flat()
    return hasMapWithoutAddress(nodes as never)
  }, [form, step])
  /* Un texto escrito en la vista previa con doble clic: se guarda como los
     demás cambios de texto (`textEdits`), y `composeElements` lo pone. */
  const writeText = (elementId: string, text: string) => setForm((f) => (f ? { ...f, textEdits: { ...f.textEdits, [elementId]: text } } : f))

  /* Con IA, la dirección sale del nombre que eligió ella y no se mueve. Desde
     un template no hay nombre hasta que se escribe: sale de ése, con una pausa
     para no preguntar en cada letra. */
  const aiName = mode !== 'ai' ? (form?.pageName.trim() ?? '') : (form?.aiBusinessName ?? '')
  useEffect(() => {
    setReservedAddress(null)
    if (!aiName) return
    let alive = true
    const timer = window.setTimeout(() => {
      void projectAddress(token, { name: aiName })
        .then((result) => { if (alive) setReservedAddress(result.subdomain) })
        .catch(() => {})
    }, mode !== 'ai' ? 450 : 0)
    return () => { alive = false; window.clearTimeout(timer) }
  }, [aiName, token, mode])

  /* La industria de los templates: la del negocio que contestó en la
     bienvenida, o todos. Se cambia arriba de la lista. */
  const [industry, setIndustry] = useState<WelcomeBusiness | 'all'>(() => {
    const business = readWelcome(userId)?.business
    return business && business !== 'other' ? business : 'all'
  })
  /* Lo que viaja con el prompt (`BriefChips`): lo último que usó, cambiable
     en las fichas de debajo del campo. Se relee al abrir: pudo cambiarlo en la
     pantalla de inicio. */
  const [brief, setBrief] = useState(() => readWelcome(userId))
  useEffect(() => { if (open) setBrief(readWelcome(userId)) }, [open, userId])
  function changeBrief(next: WelcomeAnswers) {
    saveWelcome(userId, next)
    setBrief(next)
  }
  /* Las ideas de arranque del prompt, de su industria si la dijo (`promptIdeas`). */
  /* Las fotos propias (`wizardPhotos`): se releen al abrir, como el brief,
     porque pudo elegirlas en la pantalla de inicio. */
  const photos = useWizardPhotos(userId, token, wizardPhotoLimit(plan))
  const reloadPhotos = photos.reload
  useEffect(() => { if (open) reloadPhotos() }, [open, reloadPhotos])
  const ownIdeas = industryIdeas(brief?.business)
  const fitsIndustry = (template: PublicTemplate) => industry === 'all' || templateFitsBusiness(template.bestFor, industry)

  /* El catálogo de templates de sitio, al abrir en modo template. Empieza
     puesto el primero de su industria. */
  useEffect(() => {
    if (!open || mode !== 'template' || templates) return
    let alive = true
    /* En el idioma del sitio, que es el del panel: un template en otro idioma
       llegaría con sus textos fijos sin traducir. */
    void listPublicTemplates(token, 'website', language)
      .then((list) => {
        if (!alive) return
        const sites = list.filter((item) => item.kind === 'website')
        setTemplates(sites)
        const first = sites.find(fitsIndustry) ?? sites[0]
        if (first) void chooseTemplate(first.id)
      })
      .catch(() => { if (alive) setTemplates([]) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, templates, token])

  /**
   * Elegir un template rehace el formulario con lo suyo, pero conserva lo que
   * ya escribió el cliente —su nombre, su logo y su contacto—: cambiar de
   * diseño no debería obligar a volver a llenarlos.
   */
  async function chooseTemplate(id: string) {
    setTemplateId(id)
    setTemplateLoading(true)
    try {
      const data = await getPublicTemplateData(token, id, language)
      const next = withStartRecipe(hydrateFromTemplate({
        templateId: id,
        elements: (Array.isArray(data.elements) ? data.elements : []) as ElementNode[],
        theme: data.theme,
        language,
        homeName: t('pages.home'),
      }), startRecipe)
      setForm((previous) => (previous
        ? { ...next, pageName: previous.pageName, companyName: previous.companyName, logoUrl: previous.logoUrl, contacts: previous.contacts }
        : next))
    } catch {
      setError(t('cwa.wz.templateFailed'))
    } finally {
      setTemplateLoading(false)
    }
  }

  /* El estilo, rehecho desde la receta: el mismo camino que usó el generador,
     así que lo que se ve aquí es lo que se crea. */
  function selectRecipe(recipe: CreateForm['recipe']) {
    if (!form) return
    patch({ recipe, theme: themeForRecipe(form, recipe) })
  }

  /* Una combinación entera como punto de partida: su estilo, su paleta como
     «Tu paleta» y su letra. Sin id, la que dejó la IA. Lo que se haya ajustado
     antes en colores, letra o estilo se cambia por lo de la combinación. */
  function selectCombination(styleId: string | null) {
    if (!form) return
    const recipe = styleId
      ? recipeWithBrand(comboRecipe(styleId), brandPaletteOf(comboRecipe(styleId)))
      : form.aiRecipe ?? form.recipe
    const brandPalette = recipe.palette ? { ...recipe.palette } : brandPaletteOf(recipe)
    patch({ combination: styleId ?? undefined, recipe, brandPalette, theme: themeForRecipe(form, recipe) })
  }

  async function generate(description: string) {
    setStatus('loading'); setError(null); setErrorCode(null)
    try {
      // Offline mode (bell icon): visualize the wizard with a static sample — no AI call.
      /* Lo que contestó en la bienvenida viaja aparte, como contexto: el texto
         sigue siendo sólo lo que escribió. */
      /* Las fotos, de lo guardado y no del estado: al abrir con la descripción
         ya escrita se genera en el mismo instante en que se releen. */
      const photoIds = readWizardPhotos(userId).map((photo) => photo.id)
      const draft = await draftProjectWithAi(token, description.trim(), language, welcomeBrief(readWelcome(userId)), null, photoIds)
      setGenerationCost({ charged: draft.charged === true, freeTriesLeft: typeof draft.freeTriesLeft === 'number' ? draft.freeTriesLeft : null })
      const hydrated = withStartRecipe(hydrateForm(draft), startRecipe)
      setForm(hydrated)
      setStep('info')
      /* No directo al asistente: la vista previa tarda un poco en pintar, y
         saltar a ella la enseñaba a medio cargar. Primero «Tu sitio está
         listo» (con la mascota contenta) mientras carga debajo, y luego la
         pantalla se desvanece sobre ella. */
      setLeaving(false)
      setStatus('done')
      doneTimers.current.forEach((id) => window.clearTimeout(id))
      doneTimers.current = [
        window.setTimeout(() => setLeaving(true), 1500),
        window.setTimeout(() => { setStatus('ready'); setLeaving(false) }, 1950),
      ]
      // Guardado ya: si cierra sin crear, lo retoma sin generar otro.
      saveAiDraft(userId, { prompt: description.trim(), form: hydrated, step: 'info' })
    } catch (e) {
      if (onPlanLimit && isPlanLimitError(e, 'projects')) { setStatus('ready'); onPlanLimit(); return }
      /* Traducido y nunca el cuerpo de la respuesta: antes se enseñaba el JSON
         del servidor tal cual, con llaves y comillas. */
      /* Sólo lo que el servidor marca con código tiene un texto pensado para
         quien usa el panel; un fallo sin código es técnico —«Gemini timeout»—
         y se dice con la frase general. */
      const code = serverErrorCode(e)
      setErrorCode(code)
      setError(code ? translateServerError(e, t('cwa.wz.error.lead')) : t('cwa.wz.error.lead'))
      setStatus('error')
    }
  }

  /* Se genera sola sólo si la descripción llegó de fuera. Sin ella el asistente
     abre por su primer paso —escribirla— y no gasta una llamada a la IA contra
     un texto vacío, que es lo que pasaba al abrirlo desde una tarjeta. */
  useEffect(() => {
    /* Retomar: el borrador guardado, tal como se dejó, sin llamar a la IA. */
    if (open && !startedRef.current && resume && mode === 'ai') {
      const saved = readAiDraft(userId)
      if (saved) {
        startedRef.current = true
        setPrompt(saved.prompt)
        setForm(saved.form)
        setStep((STEPS.some((item) => item.key === saved.step) ? saved.step : 'info') as StepKey)
        setStatus('ready')
        return
      }
    }
    /* La demo: su diseño tal como se dejó, sin IA. */
    if (open && !startedRef.current && mode === 'demo' && initialForm) {
      startedRef.current = true
      setForm(initialForm)
      setStep('info')
      setStatus('ready')
      return
    }
    if (open && !startedRef.current && initialPrompt.trim()) {
      startedRef.current = true
      void generate(initialPrompt)
    }
    if (!open) {
      startedRef.current = false
      setForm(null)
      setStep(firstStep)
      setPrompt(initialPrompt)
      setTemplates(null)
      setTemplateId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    const index = steps.findIndex((item) => item.key === step)
    if (index > furthest) setFurthest(index)
  }, [step, steps, furthest])
  useEffect(() => { if (!open) setFurthest(0) }, [open])

  /* Cada cambio del borrador, guardado (con una pausa): los colores, la letra,
     el nombre y el paso en que iba, para retomarlo igual. */
  useEffect(() => {
    if (!open || mode !== 'ai' || !form || status !== 'ready') return
    const timer = window.setTimeout(() => saveAiDraft(userId, { prompt, form, step }), 600)
    return () => window.clearTimeout(timer)
  }, [open, mode, form, step, status, prompt, userId])

  /* Lo que le queda, al abrir. */
  useEffect(() => {
    if (!open || mode !== 'ai') return
    let alive = true
    void getAccountUsage(token)
      .then((usage) => {
        if (!alive) return
        setPlan(usage.plan)
        const limit = usage.limits.ai_credits
        setCredits(limit === null ? null : { left: Math.max(0, limit - usage.used.aiCredits), total: limit, resetAt: usage.aiCreditsResetAt })
      })
      .catch(() => { if (alive) setCredits(null) })
    return () => { alive = false }
  }, [open, mode, token])

  if (!open) return null

  async function create() {
    if (!form || creating) return
    setCreating(true); setError(null)
    try {
      const data = composeProjectData(form)
      const name = form.pageName.trim() || form.aiBusinessName || t('cwa.defaultName')
      /* La dirección la elegimos nosotros, libre, a partir del nombre de la IA:
         pedirla aquí era fricción —y un «ya está ocupada» a mitad de crear—.
         Es la de la vista previa; si entre tanto se la llevó otro, el servidor
         toma la siguiente libre. Se cambia después en Dominio. */
      /* Sin la dirección de la vista previa (falló la consulta), el nombre: el
         servidor lo cambia por uno libre igual. */
      const subdomain = reservedAddress ?? (slugify((mode !== 'ai' ? '' : form.aiBusinessName) || name) || 'site')
      /* Las fotos que subió para la IA pasan a ser de este proyecto. */
      const assetIds = mode === 'ai' ? readWizardPhotos(userId).map((photo) => photo.id) : []
      const project = await createProject(token, name, subdomain, data, composeBusinessInfo(form), subdomain, assetIds)
      if (mode === 'ai') { clearAiDraft(userId); clearWizardPhotos(userId) }
      onCreated?.(project.id)
      window.location.href = editorHref(`?pid=${project.id}`)
    } catch (e) {
      if (onPlanLimit && isPlanLimitError(e, 'projects')) { setCreating(false); onPlanLimit(); return }
      setError(translateServerError(e, t('cwa.wz.error.createLead')))
      setCreating(false)
    }
  }

  const stepIndex = Math.max(0, steps.findIndex((item) => item.key === step))
  const nameMissing = !form || form.pageName.trim() === ''
  /* Sin nombre no se pasa del paso del nombre: es lo único obligatorio, y lo
     que firma el logo, el título y los textos. Elegir template va antes. */
  const free = (key: StepKey) => key === 'info' || key === 'template'
  const goTo = (key: StepKey) => {
    if (!free(key) && nameMissing) return
    if (key !== 'template' && !form) return
    setStep(key)
  }
  const next = () => { const following = steps[stepIndex + 1]; if (following) goTo(following.key) }
  const back = () => { const previous = steps[stepIndex - 1]; if (previous) setStep(previous.key) }

  if ((form && (status === 'ready' || status === 'done')) || mode === 'template') {
    const recipeProps = form ? { recipe: form.recipe, theme: form.theme as unknown as ThemeProjectTheme, onChange: selectRecipe } : null
    const addressName = mode !== 'ai' ? form?.pageName : (form?.aiBusinessName || form?.pageName)
    const shownAddress = `${reservedAddress ?? (slugify(addressName || '') || 'mi-sitio')}.${PUBLIC_SITE_DOMAIN}`
    /* En el paso del template, «Siguiente» sólo espera a que cargue el elegido;
       en los demás, al nombre. */
    const blocked = step === 'template' ? !form || templateLoading : nameMissing
    const wizard = (
      <div className="dash-svc-wizard cwa-wz" data-sheet={sheet} role="dialog" aria-modal="true" aria-label={t(mode !== 'ai' ? 'cwa.wz.dialogAriaTemplate' : 'cwa.wz.dialogAria')}>
        {/* La misma barra que los asistentes de oferta, servicio y página: la
            marca, el raíl de pasos en medio y la salida. El asistente vivía
            en su propio armazón —título suelto, pasos sin raíl, controles de
            otra escala— y al abrirlo parecía otra aplicación. */}
        <WizardTopBar
          onBack={onExit ?? onClose}
          onClose={onClose}
          busy={creating}
          center={(
            <nav className="dash-svc-wizard-progress" aria-label={t('cwa.wz.progressAria')}>
              <div className="dash-svc-wizard-rail" ref={railRef}>
                <span className="dash-svc-wizard-rail-thumb" aria-hidden="true" />
                {steps.map(({ key, labelKey, Icon }, index) => (
                  <button
                    type="button"
                    key={key}
                    className={`${index === stepIndex ? 'is-current' : ''}${index < stepIndex ? ' is-done' : ''}`}
                    /* En orden: se vuelve a los ya vistos, no se salta a los que
                       faltan. Crear es el último paso, no un atajo. */
                    disabled={index > furthest || (!free(key) && nameMissing) || (key !== 'template' && !form)}
                    aria-current={index === stepIndex ? 'step' : undefined}
                    onClick={() => goTo(key)}
                  >
                    <span>{index < stepIndex ? <IconCheck size={13} /> : <Icon size={14} />}</span>
                    <em>{t(labelKey)}</em>
                  </button>
                ))}
              </div>
            </nav>
          )}
        />

        <div className="dash-svc-wizard-body cwa-wz-body">
          <form
            ref={formRef}
            className="dash-svc-wizard-form cwa-wz-form"
            onSubmit={(event) => {
              event.preventDefault()
              if (blocked) return
              if (stepIndex < steps.length - 1) next(); else void create()
            }}
          >
            {/* Lo que se desplaza es sólo el paso; el pie con Atrás y Siguiente
                queda fuera, siempre a la vista. Con `sticky` dentro del mismo
                desplazamiento dependía de cómo se anidaran las cajas. */}
            <SheetHandle
              sheet={sheet}
              formRef={formRef}
              label={`${t('cwa.wz.kicker', { n: stepIndex + 1, total: steps.length })} · ${t(steps[stepIndex]?.labelKey ?? '')}`}
              defaultOpen={sheetFor(step)}
              onChange={setSheet}
            />
            <div className="cwa-wz-scroll" ref={scrollRef}>
            <div className="dash-svc-wizard-form-inner">
              <section className="dash-svc-panel-step" key={step}>
                <span className="dash-svc-step-kicker">{t('cwa.wz.kicker', { n: stepIndex + 1, total: steps.length })}</span>
                <h2>{t(`cwa.wz.${step}.title`)}</h2>
                <p>{t(step === 'info' && mode === 'template' ? 'cwa.wz.info.leadTemplate' : step === 'info' && mode === 'demo' ? 'cwa.wz.info.leadDemo' : `cwa.wz.${step}.lead`)}</p>

                {step === 'template' ? (
                  <TemplateChooser
                    templates={templates}
                    selectedId={templateId}
                    loadingId={templateLoading ? templateId : null}
                    onSelect={(id) => { if (id !== templateId) void chooseTemplate(id) }}
                    industry={industry}
                    onIndustry={(next) => {
                      setIndustry(next)
                      /* Al cambiar de industria, la vista previa pasa al primero
                         de la nueva si el elegido ya no está en la lista. */
                      const list = (templates ?? []).filter((item) => next === 'all' || templateFitsBusiness(item.bestFor, next))
                      if (list.length && !list.some((item) => item.id === templateId)) void chooseTemplate(list[0]!.id)
                    }}
                  />
                ) : null}

                {form && recipeProps ? (<>
                {step === 'info' ? (
                  <>
                    <div className="dash-svc-group">
                      <label className={`dash-svc-field${nameMissing ? ' is-invalid' : ''}`}>
                        <span>{t('cwa.businessName')} <b>*</b></span>
                        {/* El nombre que eligió la IA, ya puesto. Cambiarlo lo
                            cambia en todo: el título, el logo de la barra y los
                            textos que lo nombran. */}
                        <input
                          autoFocus
                          maxLength={80}
                          value={form.pageName}
                          placeholder={t('cwa.businessNamePlaceholder')}
                          onChange={(event) => patch({ pageName: event.target.value, companyName: event.target.value })}
                        />
                        <small>{t('cwa.businessNameHint')}</small>
                      </label>
                    </div>

                    <div className="dash-svc-group">
                      <div className="dash-svc-image-field">
                        <div><strong>{t('cwa.logo')}</strong><span>{t('offers.new.optional')}</span></div>
                        {form.logoUrl ? (
                          <div className="dash-svc-image-selected">
                            <img className="is-logo" src={form.logoUrl} alt="" />
                            <div className="dash-svc-image-selected-actions">
                              <button type="button" className="dash-secondary-btn" onClick={() => setPickingLogo(true)}>{t('cwa.logoChange')}</button>
                              <button type="button" className="dash-secondary-btn" onClick={() => patch({ logoUrl: '' })}><IconTrash size={14} /> {t('cwa.logoRemove')}</button>
                            </div>
                          </div>
                        ) : (
                          <button type="button" className="dash-svc-image-drop" onClick={() => setPickingLogo(true)}>
                            <IconPhotoPlus size={18} />
                            <span><strong>{t('cwa.logoUpload')}</strong></span>
                          </button>
                        )}
                        <p className="cwa-wz-foot">{t('cwa.logoHint')}</p>
                      </div>
                    </div>

                  </>
                ) : null}

                {step === 'combination' ? <CombinationStep
                    value={form.combination ?? null}
                    aiTheme={(form.aiRecipe ? themeForRecipe(form, form.aiRecipe) : form.theme) as unknown as ThemeProjectTheme}
                    aiStyleId={(form.aiRecipe ?? form.recipe).designStyleId}
                    baseTheme={combinationBaseOf(form)}
                    onChange={selectCombination}
                    origin={mode === 'demo' ? { badge: t('demo.combination.badge'), hint: t('demo.combination.hint') } : undefined}
                  /> : null}
                {step === 'style' ? <StylePresetsStep {...recipeProps} aiStyleId={form.aiRecipe?.designStyleId} comboStyleId={form.combination} /> : null}
                {step === 'motion' ? <MotionStep
                    value={siteMotionOf(form)}
                    suggested={suggestedMotion(form)}
                    theme={form.theme as unknown as ThemeProjectTheme}
                    onChange={(motionStyle) => { patch({ motionStyle }); setMotionReplay((value) => value + 1) }}
                  /> : null}
                {step === 'colors' ? <ColorsStep
                    {...recipeProps}
                    brand={form.brandPalette}
                    /* Sólo si hubo IA: un sitio que sale de una plantilla no
                       tiene «Creada por la IA». */
                    aiPalette={form.aiPalette ?? (form.aiPaletteId
                      ? brandPaletteOf({ ...(form.recipe.designStyleId ? { designStyleId: form.recipe.designStyleId } : {}), paletteId: form.aiPaletteId })
                      : null)}
                    aiPaletteId={form.aiPaletteId ?? null}
                    preset={stylePreset(form, t)}
                    onBrandChange={(next, paletteId) => {
                      /* Con `paletteId`, se acaba de elegir una paleta entera: queda
                         marcada como la de partida. Sin él, se editó un color. */
                      const recipe = recipeWithBrand(paletteId ? { ...form.recipe, paletteId } : form.recipe, next)
                      patch({ brandPalette: next, recipe, theme: themeForRecipe(form, recipe) })
                    }}
                  /> : null}
                {step === 'fonts' ? <FontsStep {...recipeProps} aiRecipe={form.aiRecipe} /> : null}
                {step === 'sections' ? (
                  <WizardSectionsStep
                    token={token}
                    form={form}
                    onChange={setForm}
                    activeRootId={previewSection}
                    onReveal={(rootId) => setReveal((last) => ({ rootId, nonce: (last?.nonce ?? 0) + 1 }))}
                    page={previewPage}
                    onPage={setPreviewPage}
                  />
                ) : null}

                {step === 'contact' ? (
                  <>
                    <WizardContactStep contacts={form.contacts} onChange={(contacts) => patch({ contacts })} missing={missingContacts} mapNeedsAddress={mapNeedsAddress} hours={form.hours ?? []} onHoursChange={(hours) => patch({ hours })} pageShowsHours={pageShowsHours} locations={form.locations ?? []} onLocationsChange={(locations) => patch({ locations })} />
                  </>
                ) : null}
                </>) : null}
              </section>

              {error ? <p className="dash-svc-error" role="alert">{error}</p> : null}
            </div>
            </div>

            {/* Fuera de lo que se desplaza, justo encima del pie: es lo que hay
                que leer antes de seguir, y al fondo del paso pasaba de largo. */}
            {step === 'info' && form && (form.trimmed.pages + form.trimmed.sections > 0 || form.exampleContent) ? (
              <div className="cwa-wz-notes cwa-wz-notes--pinned">
                {form.exampleContent ? <p><IconAlertTriangle size={15} aria-hidden="true" />{t('cwa.exampleContent')}</p> : null}
                {form.trimmed.pages + form.trimmed.sections > 0
                  ? <p className="is-neutral"><IconInfoCircle size={15} aria-hidden="true" />{t('cwa.trimmed')}</p>
                  : null}
              </div>
            ) : null}
            {/* En el último paso, muestra el costo de crear pegado al botón de
                «Crear», que es donde se decide. */}
            {step === 'contact' && form && mode === 'ai' && credits !== null ? (
              <div className="cwa-wz-notes cwa-wz-notes--pinned">
                {/* Lo que queda, en grande, y cuándo vuelve. Crear gasta una,
                   salvo que ésta ya se cobrara al generarla (pasó de las
                   pruebas gratis): entonces se dice que ya está descontada. */}
                <div className={`cwa-wz-credits-card${credits.left > 0 ? '' : ' is-empty'}`}>
                  <span className="cwa-wz-credits-count">{credits.left}</span>
                  <span className="cwa-wz-credits-copy">
                    <strong>{t(credits.left > 0 ? 'cwa.credits.leftOf' : 'cwa.credits.cardTitleNone', { total: credits.total })}</strong>
                    <span>{generationCost.charged
                      ? t('cwa.credits.charged')
                      : t(credits.left > 0 ? 'cwa.credits.usesOne' : 'cwa.credits.none')}</span>
                  </span>
                  <span className="cwa-wz-credits-reset">
                    {credits.resetAt
                      ? <><IconRefresh size={13} aria-hidden="true" />{t('cwa.credits.resets', { when: fmt.dateTime(credits.resetAt) })}</>
                      : t('cwa.credits.lifetime')}
                  </span>
                </div>
              </div>
            ) : null}

            <div className="cwa-wz-footer">
              <div className="dash-svc-step-actions">
                {stepIndex > 0
                  ? <button type="button" className="dash-secondary-btn" disabled={creating} onClick={back}><IconArrowLeft size={14} /> {t('cwa.back')}</button>
                  : <button type="button" className="dash-secondary-btn" disabled={creating} onClick={onClose}>{t('offers.new.cancel')}</button>}
                {stepIndex < steps.length - 1 ? (
                  <button type="submit" className="dash-primary-btn" disabled={blocked}>
                    {t('cwa.next')} <IconArrowRight size={14} />
                  </button>
                ) : (
                  <button type="submit" className="dash-primary-btn" disabled={creating || nameMissing}>
                    {creating ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}
                    {t(creating ? 'cwa.creating' : 'cwa.create')}
                  </button>
                )}
              </div>
            </div>
          </form>

          <aside className="dash-svc-wizard-preview cwa-wz-preview" aria-label={t('createAi.previewTitle')}>
            <div className="dash-svc-preview-heading">
              <span>{t('createAi.previewTitle')}</span>
              <em>{t('cwa.wz.previewNote')}</em>
            </div>
            {/* El navegador ocupa lo que queda de alto y la página se desplaza
                dentro: la barra de direcciones se queda quieta, como en uno de
                verdad. */}
            <div className="dash-svc-browser cwa-wz-browser">
              <div className="dash-svc-browser-bar">
                <i /><i /><i />
                <span className="dash-svc-browser-url">{shownAddress}</span>
              </div>
              {form
                ? <PreviewLive form={form} focus={step === 'contact' ? 'bottom' : 'top'} reveal={reveal} onSection={setPreviewSection} page={previewPage} onPage={setPreviewPage} onText={writeText} motionReplay={motionReplay} minDesignWidth={compact ? 0 : undefined} />
                : <div className="cwa-wz-preview-wait"><IconLoader2 className="dash-spin" size={20} /></div>}
            </div>
          </aside>
        </div>

        {pickingLogo ? (
          <ImageLibraryPickerDialog
            token={token}
            onSelect={(url) => { patch({ logoUrl: url }); setPickingLogo(false) }}
            onClose={() => setPickingLogo(false)}
          />
        ) : null}
      </div>
    )
    if (status !== 'done') return wizard
    /* Recién generado: el asistente ya está montado debajo (su vista previa
       carga) y encima, la pantalla de carga diciendo «listo» antes de
       desvanecerse. */
    return (
      <>
        {wizard}
        <div className={`cwa-fullscreen cwa-done-overlay${leaving ? ' is-leaving' : ''}`} aria-hidden={leaving}>
          <header className="cwa-topbar">
            <div className="dash-topbar-brand"><BrandBackPill onClick={onExit ?? onClose} /></div>
            <button className="cwa-close dash-topbar-close" type="button" onClick={onClose} aria-label={t('common.close')}><IconX size={16} /></button>
          </header>
          <div className="cwa-frame">
            <div className="cwa-loading cwa-wz-loading is-done">
              <AiMascot cheer={1} />
              <div className="cwa-prompt-copy">
                <h2 key="done" className="cwa-done-title">
                  <span className="cwa-done-check" aria-hidden="true"><IconCheck size={16} stroke={2.6} /></span>
                  {t('cwa.created')}
                </h2>
                <p className="cwa-step-line" aria-live="polite"><span className="cwa-done-sub">{t('cwa.createdSub')}</span></p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="cwa-fullscreen" role="dialog" aria-modal="true">
      {/* La misma barra del panel: logo a la izquierda, acciones a la derecha y
          sin fondo propio, porque el marco de abajo ya delimita el área de
          trabajo. */}
      <header className="cwa-topbar">
        {/* La MISMA pastilla que la pantalla de empezar: es la misma barra y no
            puede medir distinto según por qué camino se haya entrado.

            Sale al panel; la X de la derecha vuelve a «Comienza tu proyecto».
            Son dos salidas a distancias distintas. */}
        <div className="dash-topbar-brand">
          <BrandBackPill onClick={onExit ?? onClose} />
        </div>
        <button className="cwa-close dash-topbar-close" type="button" onClick={onClose} aria-label={t('common.close')}><IconX size={16} /></button>
      </header>

      {/* El marco grande, como el del panel: todo lo que sigue vive dentro. */}
      <div className="cwa-frame">

      {status === 'loading' ? (
        <div className="cwa-loading cwa-wz-loading">
          <AiMascot working />
          <div className="cwa-prompt-copy">
            <h2>{t('cwa.generating')}</h2>
            {/* El paso de ahora: con `key`, cada paso nuevo vuelve a montarse y
                entra con su animación (sube y se enfoca); mientras dura, un
                brillo lo recorre para decir «sigue trabajando». */}
            <p className="cwa-step-line" aria-live="polite">
              <span key={generatingAt} className="cwa-step">{t(GENERATING_STEPS[generatingAt % GENERATING_STEPS.length]!)}</span>
            </p>
          </div>
        </div>
      ) : status === 'error' ? (
        errorCode === 'ai.dailyGenerationLimit' ? (
          <div className="cwa-wz-error">
            <span className="cwa-wz-error-icon is-calm" aria-hidden="true"><IconClockHour4 size={24} /></span>
            <h2>{t('cwa.wz.limit.title')}</h2>
            <p>{t('cwa.wz.limit.lead')}</p>
            <div className="cwa-wz-error-actions">
              <button className="cwa-primary" type="button" onClick={onClose}>{t('cwa.wz.limit.other')}</button>
            </div>
            <small>{t('cwa.wz.limit.foot')}</small>
          </div>
        ) : (
          <div className="cwa-wz-error">
            <span className="cwa-wz-error-icon" aria-hidden="true"><IconAlertCircle size={24} /></span>
            <h2>{t('cwa.wz.error.title')}</h2>
            <p>{error}</p>
            <div className="cwa-wz-error-actions">
              <button
                className="dash-secondary-btn"
                type="button"
                onClick={() => {
                  setError(null); setErrorCode(null)
                  /* Si la descripción se escribió en «Comienza tu proyecto», editarla
                     es volver allí: esa pantalla sigue montada debajo con el texto
                     intacto. Abrir aquí la copia del mismo campo era llevar a una
                     página que nunca se había visto. */
                  if (initialPrompt.trim()) { onClose(); return }
                  setStatus('ready')
                }}
              >
                {t('cwa.wz.error.edit')}
              </button>
              <button className="cwa-primary" type="button" onClick={() => void generate(prompt)}>{t('domain.retry')}</button>
            </div>
          </div>
        )
      ) : (
        /* El primer paso del asistente: contar qué se quiere.
           A pantalla completa y con una sola cosa que hacer, porque de este
           texto sale todo lo demás — cuanto mejor se escriba, menos hay que
           corregir después. */
        <div className="cwa-prompt">
          <AiMascot />
          <div className="cwa-prompt-copy">
            <h2>{t('cwa.title')}</h2>
            <IdeaShuffle ideas={ownIdeas ?? PROMPT_TEMPLATES} onIdea={showExample} />
          </div>
          <PromptComposer
            value={prompt}
            fieldRef={promptFieldRef}
            onChange={setPrompt}
            revealKey={revealKey}
            onSubmit={() => void generate(prompt)}
            brief={brief}
            onBrief={changeBrief}
            photos={photos}
          />
        </div>
      )}
    </div>
    </div>
  )
}

/** La página tal como se va a crear, pintada por el editor. */
export function PreviewLive({ form, focus, reveal, onSection, page, onPage, onText, motionReplay, minDesignWidth, initialScroll, goTo, onShown, onLink }: {
  form: CreateForm
  focus: 'top' | 'bottom'
  reveal: { rootId: string; nonce: number } | null
  onSection: (rootId: string | null) => void
  /** La página que se enseña (0 = la portada). */
  page: number
  onPage: (page: number) => void
  /** Un texto escrito en la vista previa (doble clic). Sin él, no se guarda. */
  onText?: ((elementId: string, text: string) => void) | undefined
  motionReplay: number
  /** Ver `SitePreview`: el modo diseño de veltica.com pinta a tamaño real y en el punto donde se leía. */
  minDesignWidth?: number | undefined
  initialScroll?: number | undefined
  goTo?: { fraction: number; nonce: number } | null | undefined
  onShown?: (() => void) | undefined
  onLink?: ((href: string) => void) | undefined
}) {
  const data = useMemo(() => composeProjectData(form), [form])
  const extra = extraPagesOf(form).find((item) => item.index === page)
  /* Una página que no se incluye se ve igual, para decidir; fuera de la barra. */
  const previewPage = useMemo(() => (extra && !extra.included ? composePreviewPage(form, page) : null), [extra, form, page])
  const pageSlug = extra?.slug || '/'
  return (
    <SitePreview
      data={data}
      language={form.language}
      focus={focus}
      reveal={reveal}
      onSection={onSection}
      pageSlug={pageSlug}
      previewPage={previewPage}
      motionReplay={motionReplay}
      onText={onText}
      {...(minDesignWidth !== undefined ? { minDesignWidth } : {})}
      initialScroll={initialScroll}
      goTo={goTo ?? null}
      onShown={onShown}
      onLink={onLink}
      onPage={(slug) => onPage(slug === '/' ? 0 : extraPagesOf(form).find((item) => item.slug === slug)?.index ?? 0)}
    />
  )
}

/**
 * Los templates de sitio, como en «Nueva página» del editor: la lista a la
 * izquierda y el elegido en la vista previa de la derecha. Se elige uno con un
 * clic; el primero ya viene puesto para que la vista previa nunca esté vacía.
 */
function TemplateChooser({ templates, selectedId, loadingId, onSelect, industry, onIndustry }: {
  templates: PublicTemplate[] | null
  selectedId: string | null
  loadingId: string | null
  onSelect: (id: string) => void
  /** La industria por la que se filtra: la de la bienvenida al abrir. */
  industry: WelcomeBusiness | 'all'
  onIndustry: (industry: WelcomeBusiness | 'all') => void
}) {
  const { t } = useI18n()
  if (!templates) {
    return <p className="cwa-wz-templates-state"><IconLoader2 className="dash-spin" size={15} /> {t('cwa.wz.template.loading')}</p>
  }
  if (!templates.length) return <p className="cwa-wz-templates-state">{t('cwa.wz.template.empty')}</p>
  const matching = industry === 'all' ? templates : templates.filter((item) => templateFitsBusiness(item.bestFor, industry))
  /* Sin templates de esa industria todavía, se enseñan todos y se dice. */
  const shown = matching.length ? matching : templates
  const industries = (['all', ...WELCOME_BUSINESSES.filter((key) => key !== 'other')] as const)
  return (
    <>
    <div className="cwa-wz-industries" role="radiogroup" aria-label={t('cwa.wz.template.industry')}>
      {industries.map((key) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={industry === key}
          className={`cwa-chip${industry === key ? ' active' : ''}`}
          onClick={() => onIndustry(key)}
        >
          {t(key === 'all' ? 'cwa.wz.template.all' : WELCOME_BUSINESS_LABELS[key])}
        </button>
      ))}
    </div>
    {!matching.length ? <p className="cwa-wz-templates-state">{t('cwa.wz.template.noneForIndustry')}</p> : null}
    <div className="cwa-wz-templates" role="listbox" aria-label={t('cwa.step.template')}>
      {shown.map((template) => {
        const active = template.id === selectedId
        return (
          <button
            key={template.id}
            type="button"
            role="option"
            aria-selected={active}
            className={`cwa-wz-template${active ? ' is-active' : ''}`}
            onClick={() => onSelect(template.id)}
          >
            <span className="cwa-wz-template-thumb">
              {template.thumbnailUrl ? <img src={template.thumbnailUrl} alt="" loading="lazy" /> : <IconLayoutBoard size={22} aria-hidden="true" />}
              {loadingId === template.id ? <span className="cwa-wz-template-busy"><IconLoader2 className="dash-spin" size={16} /></span> : null}
            </span>
            <span className="cwa-wz-template-copy">
              <strong>{template.name}</strong>
              {template.description ? <small>{template.description}</small> : null}
            </span>
            {active ? <IconCheck className="cwa-wz-template-check" size={15} aria-hidden="true" /> : null}
          </button>
        )
      })}
    </div>
    </>
  )
}

/* ── La hoja del asistente en el celular ─────────────────────────────────── */

type WizardSheet = 'peek' | 'half' | 'full'

/** Los pasos de escribir abren la hoja entera; los de elegir, a media altura (se ve la página). */
function sheetFor(key: StepKey): WizardSheet {
  return key === 'info' || key === 'contact' || key === 'template' ? 'full' : 'half'
}

const COMPACT_QUERY = '(max-width: 1088px)'

/** Las filas de opciones de la hoja (ver el CSS de `.cwa-wz[data-sheet="half"]`). */
const SHEET_SLIDERS = '.cwa-looks, .cwa-wz-styles, .cwa-wz-palette-row, .cwa-wz-font-strip, .cwa-motions'

/** Si el asistente va en modo hoja (la misma medida en que antes se escondía la vista previa). */
function useCompactWizard(): boolean {
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(COMPACT_QUERY).matches === true)
  useEffect(() => {
    const query = window.matchMedia?.(COMPACT_QUERY)
    if (!query) return
    const update = () => setCompact(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return compact
}

/**
 * El asa de la hoja: tocarla la pliega o la abre a la altura de su paso;
 * arrastrarla la sigue con el dedo y, al soltar, se queda en la altura más
 * cercana. Sólo se ve en el celular (ver `.cwa-wz-sheet-handle`).
 */
function SheetHandle({ sheet, formRef, label, defaultOpen, onChange }: {
  sheet: WizardSheet
  formRef: { current: HTMLFormElement | null }
  label: string
  defaultOpen: WizardSheet
  onChange: (next: WizardSheet) => void
}) {
  const { t } = useI18n()
  const drag = useRef<{ y: number; height: number; moved: boolean } | null>(null)
  const heights = () => {
    const body = formRef.current?.parentElement?.getBoundingClientRect().height ?? window.innerHeight
    return { peek: 128, half: body * 0.55, full: body - 12 }
  }
  return (
    <button
      type="button"
      className="cwa-wz-sheet-handle"
      aria-expanded={sheet !== 'peek'}
      aria-label={sheet === 'peek' ? t('cwa.sheet.open') : t('cwa.sheet.close')}
      onPointerDown={(event) => {
        const form = formRef.current
        if (!form) return
        drag.current = { y: event.clientY, height: form.getBoundingClientRect().height, moved: false }
        event.currentTarget.setPointerCapture(event.pointerId)
      }}
      onPointerMove={(event) => {
        const current = drag.current
        const form = formRef.current
        if (!current || !form) return
        const dy = event.clientY - current.y
        if (!current.moved && Math.abs(dy) < 6) return
        if (!current.moved) {
          current.moved = true
          /* Desde plegada, la hoja pasa a su altura ya al empezar a subirla:
             el paso se ve mientras se arrastra, no en blanco hasta soltar. */
          if (sheet === 'peek') onChange(defaultOpen)
        }
        const { peek, full } = heights()
        form.style.transition = 'none'
        form.style.maxHeight = 'none'
        form.style.height = `${Math.max(peek, Math.min(full, current.height - dy))}px`
      }}
      onPointerUp={() => {
        const current = drag.current
        const form = formRef.current
        drag.current = null
        if (!current || !form) return
        if (!current.moved) { onChange(sheet === 'peek' ? defaultOpen : 'peek'); return }
        const now = form.getBoundingClientRect().height
        const stops = heights()
        const nearest = (Object.keys(stops) as WizardSheet[]).reduce((best, key) => (Math.abs(stops[key] - now) < Math.abs(stops[best] - now) ? key : best), 'half' as WizardSheet)
        form.style.transition = ''
        form.style.height = ''
        form.style.maxHeight = ''
        onChange(nearest)
      }}
      onPointerCancel={() => {
        drag.current = null
        if (formRef.current) { formRef.current.style.transition = ''; formRef.current.style.height = ''; formRef.current.style.maxHeight = '' }
      }}
    >
      <span className="cwa-wz-sheet-grip" aria-hidden="true" />
      <span className="cwa-wz-sheet-label">{label}</span>
    </button>
  )
}
